import { useMemo, useState } from "react";
import client from "../api/client";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useFinancialYear } from "../context/FinancialYearContext";
import { useAuth } from "../context/AuthContext";

const styles = {
  page: { fontSize: 11, minHeight: "100%", overflow: "visible", padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  tableWrap: {
    maxHeight: 430,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 850, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  badge: {
    fontSize: 10,
    padding: "4px 7px",
    borderRadius: 20,
    fontWeight: 600,
  },
};

export default function TrialBalancePage() {
  const { financialYear } = useFinancialYear();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [fromDate, setFromDate] = useState(financialYear.fromDate);
  const [toDate, setToDate] = useState(financialYear.toDate);
  const [loading, setLoading] = useState(false);

  const loadReport = async () => {
    try {
      setLoading(true);
      const res = await client.get(
        `/finance/trial-balance?fromDate=${fromDate}&toDate=${toDate}${user?.companyId ? `&companyId=${user.companyId}` : ""}`,
      );
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      alert("Failed to load trial balance");
    } finally {
      setLoading(false);
    }
  };

  const formatBalance = (amount, type) => {
    const n = Number(amount || 0);
    if (n === 0) return "0.00";
    return `${n.toFixed(2)} ${type}`;
  };

  const totalDebit = useMemo(
    () => rows.reduce((sum, r) => sum + Number(r.TotalDebit || 0), 0),
    [rows],
  );

  const totalCredit = useMemo(
    () => rows.reduce((sum, r) => sum + Number(r.TotalCredit || 0), 0),
    [rows],
  );

  const isBalanced = totalDebit === totalCredit;

  const exportToExcel = () => {
    if (!rows.length) return alert("No data to export");

    const data = rows.map((r) => ({
      Code: r.AccountCode,
      Account: r.AccountName,
      Debit: Number(r.TotalDebit || 0).toFixed(2),
      Credit: Number(r.TotalCredit || 0).toFixed(2),
      Balance: formatBalance(r.BalanceAmount, r.BalanceType),
    }));

    data.push({
      Code: "",
      Account: "Total",
      Debit: totalDebit.toFixed(2),
      Credit: totalCredit.toFixed(2),
      Balance: isBalanced ? "Balanced" : "Mismatch",
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TrialBalance");
    XLSX.writeFile(workbook, "TrialBalance.xlsx");
  };

  const exportToPDF = () => {
    if (!rows.length) return alert("No data to export");

    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Trial Balance", 14, 15);
    doc.setFontSize(9);
    doc.text(`From Date: ${fromDate || "-"}`, 14, 23);
    doc.text(`To Date: ${toDate || "-"}`, 80, 23);

    autoTable(doc, {
      startY: 28,
      head: [["Code", "Account", "Debit", "Credit", "Balance"]],
      body: rows.map((r) => [
        r.AccountCode,
        r.AccountName,
        Number(r.TotalDebit || 0).toFixed(2),
        Number(r.TotalCredit || 0).toFixed(2),
        formatBalance(r.BalanceAmount, r.BalanceType),
      ]),
      foot: [
        [
          "",
          "Total",
          totalDebit.toFixed(2),
          totalCredit.toFixed(2),
          isBalanced ? "Balanced" : "Mismatch",
        ],
      ],
      styles: { fontSize: 8 },
      headStyles: { fontSize: 8 },
      footStyles: { fontSize: 8 },
    });

    doc.save("TrialBalance.pdf");
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-calculator me-2"></i>
            Trial Balance
          </h6>
          <div style={styles.sub}>
            Debit/Credit verification report for selected financial period
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2 align-items-end">
            <Field label="From Date" className="col-md-3">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Field>

            <Field label="To Date" className="col-md-3">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Field>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-success btn-sm"
                style={styles.btn}
                onClick={loadReport}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>
                {loading ? "Loading..." : "Load"}
              </button>
            </div>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-outline-primary btn-sm"
                style={styles.btn}
                onClick={exportToExcel}
                disabled={!rows.length}
              >
                <i className="bi bi-file-earmark-excel me-1"></i>
                Excel
              </button>
            </div>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-outline-danger btn-sm"
                style={styles.btn}
                onClick={exportToPDF}
                disabled={!rows.length}
              >
                <i className="bi bi-file-earmark-pdf me-1"></i>
                PDF
              </button>
            </div>
          </div>

          <div
            style={{
              fontSize: 10,
              color: "#6c757d",
              marginBottom: 6,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>
              Accounts: <strong>{rows.length}</strong>
            </span>
            <span>
              Status:{" "}
              <strong style={{ color: isBalanced ? "#198754" : "#dc3545" }}>
                {isBalanced ? "Balanced" : "Mismatch"}
              </strong>
            </span>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  <th style={{ ...styles.th, minWidth: 120 }}>Code</th>
                  <th style={{ ...styles.th, minWidth: 280 }}>Account Name</th>
                  <th
                    className="text-end"
                    style={{ ...styles.th, minWidth: 130 }}
                  >
                    Debit
                  </th>
                  <th
                    className="text-end"
                    style={{ ...styles.th, minWidth: 130 }}
                  >
                    Credit
                  </th>
                  <th
                    className="text-end"
                    style={{ ...styles.th, minWidth: 130 }}
                  >
                    Balance
                  </th>
                </tr>
              </thead>

              <tbody>
                {!rows.length ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      No data found
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={index}>
                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        {row.AccountCode}
                      </td>
                      <td style={styles.td}>{row.AccountName}</td>
                      <td className="text-end" style={styles.td}>
                        {Number(row.TotalDebit || 0).toFixed(2)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {Number(row.TotalCredit || 0).toFixed(2)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        <span
                          className={`badge ${
                            row.BalanceType === "Dr"
                              ? "text-bg-success"
                              : row.BalanceType === "Cr"
                                ? "text-bg-danger"
                                : "text-bg-secondary"
                          }`}
                          style={styles.badge}
                        >
                          {formatBalance(row.BalanceAmount, row.BalanceType)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              <tfoot>
                <tr>
                  <td
                    colSpan="2"
                    style={{
                      ...styles.td,
                      fontWeight: 700,
                      background: "#f8f9fa",
                    }}
                  >
                    Total
                  </td>
                  <td
                    className="text-end"
                    style={{ ...styles.td, fontWeight: 700 }}
                  >
                    {totalDebit.toFixed(2)}
                  </td>
                  <td
                    className="text-end"
                    style={{ ...styles.td, fontWeight: 700 }}
                  >
                    {totalCredit.toFixed(2)}
                  </td>
                  <td className="text-end" style={styles.td}>
                    <span
                      className={`badge ${isBalanced ? "text-bg-success" : "text-bg-danger"}`}
                      style={styles.badge}
                    >
                      {isBalanced ? "Balanced" : "Mismatch"}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}
